<template>
  <Teleport to="body">
    <div v-if="applicant" class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-xl m-4 overflow-hidden">
        
        <!-- Header -->
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-[#035a76] to-[#0a7c9e]">
          <div>
            <h3 class="text-lg font-semibold text-white">Employee Checklist</h3>
            <p class="text-xs text-white/70">Track progress & required steps</p>
          </div>

          <button 
            @click="$emit('close')" 
            class="text-white/70 hover:text-white transition"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="px-6 py-5 space-y-4">
          
          <!-- Applicant Card -->
          <div class="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <!-- <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Applicant</p> -->
            <p class="font-semibold text-gray-800 dark:text-white">
              {{ applicantName || applicant.full_name }}
            </p>
          </div>

          <!-- Checklist -->
          <div class="space-y-2 max-h-80 overflow-y-auto pr-1">
            <div 
              v-for="process in processes" 
              :key="process.id"
              @click="toggleProcess(process.id)"
              class="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200"
              :class="checkedProcesses.includes(process.id)
                ? 'bg-[#e6f4f8] border-[#74bd54] dark:bg-[#0f2e38]'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-[#74bd54]'"
            >
              <input
                type="checkbox"
                :id="'process-' + process.id"
                v-model="checkedProcesses"
                :value="process.id"
                class="mt-1 h-4 w-4 text-[#74bd54] focus:ring-[#74bd54] border-gray-300 rounded"
              />

              <div>
                <p class="text-sm font-medium text-gray-800 dark:text-gray-100">
                  {{ process.name }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ process.description }}
                </p>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          
          <!-- Progress indicator -->
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ checkedProcesses.length }} / {{ processes.length }} completed
          </p>

          <div class="flex space-x-3">
            <button 
              @click="$emit('close')" 
              class="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            >
              Cancel
            </button>

            <button 
              @click="saveChecklist" 
              class="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#74bd54] text-white hover:bg-[#63a43f] transition shadow-sm"
            >
              <!-- Spinner -->
              <svg 
                v-if="isLoading" 
                class="animate-spin h-4 w-4 text-white" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  class="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  stroke-width="4"
                />
                <path 
                  class="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>

              <span>{{ isLoading ? 'Saving...' : 'Save Changes' }}</span>
            </button>
          </div>

        </div>

      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '@/services/api';
const props = defineProps({
  applicant: Object
});

const emit = defineEmits(['close', 'save']);

const processes = ref([]);
const checkedProcesses = ref([]);
const applicantName = ref('')

import { useToast } from 'vue-toastification'
const toast = useToast()
const isLoading = ref(false)
// Load process types
const loadProcesses = async () => {
  try {
    const res = await api.get('ProcessTypes.php'); 
    processes.value = res.data;
  } catch (err) {
    console.error('Error fetching processes', err);
  }
};
const toggleProcess = (id) => {
  if (checkedProcesses.value.includes(id)) {
    checkedProcesses.value = checkedProcesses.value.filter(p => p !== id)
  } else {
    checkedProcesses.value.push(id)
  }
}
// Pre-fill checked processes for this applicant
const loadApplicantProcesses = async () => {
  try {
    const res = await api.get(`CheckList.php?applicant_id=${props.applicant.id}`);
    checkedProcesses.value = res.data.map(p => p.process_type_id);
     if (res.data.length > 0) {
      applicantName.value = res.data[0].full_name;
    }
  } catch (err) {
    console.error('Error fetching applicant processes', err);
  }
};

onMounted(() => {
  loadProcesses();
  loadApplicantProcesses();
});

// Save the checklist
// const saveChecklist = async () => {
  
//   try {
//     await api.post('CheckList.php', {
//       applicant_id: props.applicant.id,
//       process_type_ids: checkedProcesses.value
//     });
//     emit('save', checkedProcesses.value);
//     emit('close');
//   } catch (err) {
//     console.error('Error saving checklist', err);
//   }
// };
const saveChecklist = async () => {
  if (isLoading.value) return;
  isLoading.value = true;

  const MIN_LOADING_MS = 2000;
  const startTime = Date.now();

  // Show loading toast immediately
  let loadingToastId = toast.info("Saving checklist...", { timeout: false });

  try {
    const response = await api.post('CheckList.php', {
      applicant_id: props.applicant.id,
      process_type_ids: checkedProcesses.value
    });

    // Check for backend error
    if (response.data?.status === "error") {
      throw new Error(response.data.message || "Failed to save checklist");
    }

    // Emit events if successful
    emit('save', checkedProcesses.value);
    emit('close');

    // Show success toast
    toast.success("Checklist saved successfully!");

  } catch (err) {
    const errorMsg = err.response?.data?.message || err.message || "An unexpected error occurred";
    toast.error(errorMsg);
  } finally {
    // Ensure minimum loading duration
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));

    // Dismiss loading toast
    toast.dismiss(loadingToastId);

    isLoading.value = false;
  }
};
</script>